import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { CSSTransition } from 'react-transition-group';
// queries
import { CROSS_SELL_BASKET_PRODUCTS_MARKETING } from '../../queries/product';
// context
import { GlobalContext } from '@store/global-state';
// components
import Modal from '../modal';
import ModalBody from '../modal/modal-body';
import Loader from '../loader';
import { MODAL_TYPE } from '@components/modal/constants';
import SubmitButton from '@components/submit-button';
import { getMarketingMediaUrl } from '@src/config/content.config';

// hooks
import useGetProductPlansData from '@src/features/shop/hooks/useGetProductPlansData';
// styles
import styles from './modal-cross-sell-single.module.scss';
import fadeTransition from '../../styles/fade.module.scss';
import {
  getEventId,
  trackEvent,
  trackFacebook,
  trackPinterest,
  trackReddit,
  trackTiktok,
} from '@services/tracking/tracking';
import { appendStoredTrackingParamsToUrl } from '@src/utils/shopify-tracking-params';
//types
import { ICrossSellBasketProductsQueryResponse } from '@src/types/query-response';

// Redirect to Shopify cart with the given variant ID (stored in sku field)
const redirectToShopifyCart = (variantId: string) => {
  const shopifyHost = process.env.GATSBY_SHOPIFY_HOST || 'vitl.com';
  window.location.href = appendStoredTrackingParamsToUrl(`https://${shopifyHost}/cart/${variantId}:1`);
};

const ModalCrossSellSingle = () => {
  const { showCrossSellSingle, setShowCrossSellSingle } = useContext(GlobalContext);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // queries
  const { data, loading } = useQuery<ICrossSellBasketProductsQueryResponse>(
    CROSS_SELL_BASKET_PRODUCTS_MARKETING,
    {
      fetchPolicy: 'network-only',
    }
  );

  const crossSellItem = data?.content_crossSellBasket.popup[0];
  const { dataPlans, price, offerPrice } = useGetProductPlansData(
    crossSellItem?.configurableSku || '',
    null,
    'oneOff'
  );

  const getSku = () => {
    if (!dataPlans?.product_plans) return null;
    if (dataPlans.product_plans.length === 1) return dataPlans.product_plans[0].sku;
    return dataPlans.product_plans[1].sku;
  };

  const handleAddItemToBasket = () => {
    const sku = getSku();
    if (sku) {
      setIsRedirecting(true);
      redirectToShopifyCart(sku);
    }
  };

  const handleModalClose = () => {
    setShowCrossSellSingle(false);
  };

  useEffect(() => {
    if (!showCrossSellSingle) {
      return;
    }

    const eventName = 'basket_popup';
    const eventId = getEventId();

    trackReddit(eventName);
    trackFacebook(eventName, undefined, eventId);
    trackTiktok(eventName, undefined, eventId);
    trackPinterest(eventName);
    trackEvent(eventName, undefined, undefined, eventId);
  }, [showCrossSellSingle]);

  if (loading) return <Loader />;

  if (!crossSellItem) {
    setShowCrossSellSingle(false);
    return null;
  }

  return (
    <Modal type={MODAL_TYPE.crossSellSingle} handleClose={handleModalClose}>
      <ModalBody type={MODAL_TYPE.crossSellSingle}>
        <div className={styles.productDetails}>
          <img
            src={getMarketingMediaUrl(crossSellItem.imageUrl)}
            alt=""
          />
          <div className={styles.planDetails}>
            <h3 className={styles.h3}>{crossSellItem.name}</h3>
            <p>
              {offerPrice ? <s>{price} </s> : null}
              <strong>{offerPrice || price}</strong>
            </p>
          </div>
        </div>

        <div
          className={styles.itemDesc}
          dangerouslySetInnerHTML={{
            __html: crossSellItem.content,
          }}
        />

        <SubmitButton
          onClick={() => handleAddItemToBasket()}
          loading={isRedirecting}
          buttonLabel={crossSellItem.cta}
        />
      </ModalBody>
    </Modal>
  );
};

export default ModalCrossSellSingle;
